import { motion } from "motion/react";
import { panelClassName } from "@/lib/constants";
import { groupLocationsByContinent } from "@/lib/locations";
import type { PrayerLocation, PrayerSnapshot } from "@/types/prayer";

type LocationListPanelProps = {
  onSelect: (locationId: string) => void;
  selectedLocationId: string | null;
  snapshots: Record<string, PrayerSnapshot>;
};

const groupedLocations = groupLocationsByContinent();

const LocationListPanel = ({ onSelect, selectedLocationId, snapshots }: LocationListPanelProps) => (
  <motion.div
    animate={{ opacity: 1, x: 0 }}
    className={panelClassName}
    initial={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.4 }}
  >
    <div className="shrink-0 border-b border-zinc-800/50 px-5 pt-5 pb-4">
      <h2 className="text-lg font-semibold text-zinc-100">Curated Locations</h2>
      <p className="mt-1 text-xs text-zinc-500">Choose a city to see its live prayer state and refocus the globe.</p>
    </div>

    <div className="flex-1 overflow-y-auto px-4 py-3">
      {groupedLocations.map((group) => (
        <div className="mb-4" key={group.continent}>
          <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
            {group.continent}
          </p>
          <div className="flex flex-col gap-1.5">
            {group.locations.map((location: PrayerLocation) => {
              const isSelected = selectedLocationId === location.id;
              const snapshot = snapshots[location.id];
              const activePrayer = snapshot?.activeEvent
                ? snapshot.timings.find((timing) => timing.event === snapshot.activeEvent)?.label
                : null;

              return (
                <button
                  className={`group flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-zinc-800/50 bg-zinc-900/30 hover:border-zinc-700 hover:bg-zinc-800/40"
                  }`}
                  key={location.id}
                  onClick={() => onSelect(location.id)}
                  type="button"
                >
                  <div
                    className={`h-2 w-2 shrink-0 rounded-full ${isSelected ? "bg-emerald-400" : "bg-zinc-700 group-hover:bg-zinc-500"}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-200">{location.city}</span>
                      <span className="rounded px-1 py-px text-[9px] font-semibold uppercase bg-zinc-800 text-zinc-400">
                        {snapshot?.timezoneLabel ?? "TZ"}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-zinc-500">
                      {activePrayer ?? "Loading prayer state"}
                    </span>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[10px] text-zinc-400">{location.country}</div>
                    <div className="font-mono text-[10px] text-zinc-600">
                      {location.lat.toFixed(1)}°, {location.lon.toFixed(1)}°
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

export default LocationListPanel;
